import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import { useSelector } from "react-redux";
import { RootState } from "src/redux/store";
import SettingsForm, { defaultSettings, ReviewAutomationSettings } from "./SettingsForm";
import { useGetReviewAutomationSettings, useSaveReviewAutomationSettings } from "../hooks";
import { ReviewAutomationSettingsResponse } from "../types";
import DropdownPopover from "src/components/UI/DropdownPopover";
import useSiteGBP1to1Mapping, { IGBPLocation } from "../../hooks/useSiteGBP1to1Maping";
import { useGetUserAgentDetails } from "../../hooks/useGetUserAgentDetails";
import { getProfileDisabledState } from "../../hooks/useIsConfiguredToOtherSite";

interface ReviewAutomationDialogProps {
  open: boolean;
  onClose: () => void;
  accountId?: string;
  locationId?: string;
  settingsData?: ReviewAutomationSettingsResponse;
  onSaveSettings?: (settings: ReviewAutomationSettings, selectedProfile?: any) => void;
  initialSettings?: ReviewAutomationSettings;
  showProfileSelector?: boolean;
  profiles?: IGBPLocation[];
  selectedProfile?: IGBPLocation | null;
  onSelectProfile?: (profile: IGBPLocation) => void;
  isOptionDisabled?: (profile: any) => boolean;
  getOptionDisabledTooltip?: (profile: any) => string;
  isGetSettingsEnabled?: boolean;
}

export default function ReviewAutomationDialog({
  open,
  onClose,
  accountId = "",
  locationId = "",
  settingsData,
  onSaveSettings,
  initialSettings,
  showProfileSelector = false,
  profiles: customProfiles,
  selectedProfile: customSelectedProfile,
  onSelectProfile,
  isOptionDisabled: isOptionDisabledProp,
  getOptionDisabledTooltip: getOptionDisabledTooltipProp,
  isGetSettingsEnabled = true,
}: ReviewAutomationDialogProps) {
  const { _id: reduxSiteId } = useSelector(
    (state: RootState) => state?.siteAudit?.siteAuditDetails
  ) ?? {};

  const siteGBP1to1Configurations = useSiteGBP1to1Mapping({ siteId: reduxSiteId });
  const { data: userAgentDetails } = useGetUserAgentDetails(reduxSiteId);
  const localSeoApiData = userAgentDetails?.response?.local_seo;
  const reviewAutomationApiData = userAgentDetails?.response?.review_automation;

  const isOptionDisabled = (profile: any) => {
    if (isOptionDisabledProp) return isOptionDisabledProp(profile);
    return getProfileDisabledState(
      profile,
      localSeoApiData,
      reduxSiteId,
      siteGBP1to1Configurations.connectedSite?.url,
      siteGBP1to1Configurations.siteList,
      reviewAutomationApiData
    ).disabled;
  };

  const getOptionDisabledTooltip = (profile: any) => {
    if (getOptionDisabledTooltipProp) return getOptionDisabledTooltipProp(profile);
    return getProfileDisabledState(
      profile,
      localSeoApiData,
      reduxSiteId,
      siteGBP1to1Configurations.connectedSite?.url,
      siteGBP1to1Configurations.siteList,
      reviewAutomationApiData
    ).tooltip;
  };

  const profilesList = customProfiles || siteGBP1to1Configurations.GBPLocations || [];

  const defaultProfile = useMemo(() => {
    if (customSelectedProfile) return customSelectedProfile;
    if (!siteGBP1to1Configurations || siteGBP1to1Configurations.isConfigurationError) return null;
    return siteGBP1to1Configurations.connectedGBPAccount ?? null;
  }, [customSelectedProfile, siteGBP1to1Configurations]);

  const [selectedLoc, setSelectedLoc] = useState<IGBPLocation | null>(null);

  useEffect(() => {
    if (open) {
      if (defaultProfile && !isOptionDisabled(defaultProfile)) {
        setSelectedLoc(defaultProfile);
      } else if (profilesList.length > 0) {
        const firstAvailable = profilesList.find((loc: any) => !isOptionDisabled(loc));
        setSelectedLoc(firstAvailable || null);
      } else {
        setSelectedLoc(null);
      }
    }
  }, [open, defaultProfile, profilesList]);

  const handleProfileChange = (profile: IGBPLocation) => {
    setSelectedLoc(profile);
    if (onSelectProfile) {
      onSelectProfile(profile);
    }
  };

  const effectiveAccountId = selectedLoc?.accountId
    ? String(selectedLoc.accountId)
    : (accountId || '');

  const effectiveLocationId = selectedLoc?.name
    ? (selectedLoc.name.includes('/') ? selectedLoc.name.split('/')[1] : selectedLoc.name)
    : (selectedLoc?.locationId ? String(selectedLoc.locationId) : (locationId || ''));

  const {
    data: fetchedSettingsData,
    isLoading: isFetchingSettings
  } = useGetReviewAutomationSettings({
    accountId: effectiveAccountId,
    locationId: effectiveLocationId,
    siteId: reduxSiteId,
    enabled: Boolean(open && !settingsData && isGetSettingsEnabled),
  });

  const activeSettingsData = settingsData || fetchedSettingsData;

  const [settings, setSettings] = useState<ReviewAutomationSettings>(initialSettings || defaultSettings);

  useEffect(() => {
    if (initialSettings) {
      setSettings(initialSettings);
    } else if (activeSettingsData?.settings) {
      const s = activeSettingsData.settings;
      setSettings({
        ai_instructions: s.ai_instructions || "",
        tone: s.tone || "professional",
        banned_phrases: s.banned_phrases || [],
        optionalControls: {
          personalize_with_name: s.personalize_with_name ?? true,
          skip_5_star: s.skip_5_star ?? false,
          skip_short_reviews: s.skip_short_reviews ?? false,
          reply_only_with_text: s.reply_only_with_text ?? false,
          require_approval_for_1_2_star: s.require_approval_for_1_2_star ?? true,
        }
      });
    } else {
      setSettings(defaultSettings);
    }
  }, [activeSettingsData, initialSettings, open, effectiveAccountId, effectiveLocationId]);

  const { saveSettings, isLoading } = useSaveReviewAutomationSettings({
    onSuccess: () => {
      onClose();
    }
  });

  const handleSave = () => {
    if (onSaveSettings) {
      onSaveSettings(settings, selectedLoc);
      onClose();
      return;
    }

    saveSettings({
      site_id: reduxSiteId,
      accountId: effectiveAccountId,
      locationId: effectiveLocationId,
      enabled: true,
      ai_instructions: settings.ai_instructions,
      tone: settings.tone,
      banned_phrases: settings.banned_phrases,
      personalize_with_name: settings.optionalControls.personalize_with_name,
      skip_5_star: settings.optionalControls.skip_5_star,
      skip_short_reviews: settings.optionalControls.skip_short_reviews,
      reply_only_with_text: settings.optionalControls.reply_only_with_text,
      require_approval_for_1_2_star: settings.optionalControls.require_approval_for_1_2_star
    });
  };

  const handleReset = () => {
    setSettings(defaultSettings);
  };

  const isSelectedDisabled = selectedLoc ? Boolean(isOptionDisabled(selectedLoc)) : false;

  const isSaveDisabled =
    isLoading ||
    isFetchingSettings ||
    (showProfileSelector && profilesList.length > 0 && !selectedLoc) ||
    (showProfileSelector && profilesList.length === 0) ||
    (!effectiveAccountId || !effectiveLocationId) ||
    isSelectedDisabled;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      disableScrollLock
    >
      <DialogTitle sx={{
        fontSize: 25,
        fontWeight: 600,
        color: 'var(--color-primary)'
      }}>
        Review Automation Settings
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 16,
            top: 16
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {showProfileSelector && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-[#344054] mb-2">
              Select Google Business Profile
            </label>
            {profilesList && profilesList.length > 0 ? (
              <DropdownPopover
                startIcon="/static/images/icons/local-seo/google-home.svg"
                selectedValue={selectedLoc}
                options={profilesList}
                onSelect={handleProfileChange}
                isArrayOfObject={true}
                labelKey="title"
                idKey="name"
                placeholder="Select Business"
                enableSearch={profilesList.length > 5}
                searchPlaceholder="Search profiles..."
                disabled={isLoading}
                isOptionDisabled={isOptionDisabled}
                getOptionDisabledTooltip={getOptionDisabledTooltip}
                styles={{
                  filterBoxSx: {
                    width: '100%',
                    borderColor: '#D0D5DD',
                    borderRadius: '8px',
                    minHeight: '44px'
                  }
                }}
              />
            ) : (
              <div className="p-3 rounded-lg bg-[#F9FAFB] border border-[#EAECF0] text-sm text-[#667085]">
                No connected Google Business Profile found.
              </div>
            )}
          </div>
        )}

        <SettingsForm settings={settings} onChange={setSettings} />
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          pt: 2,
          gap: 2,
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            flex: 1,
            height: 48,
            borderRadius: "8px",
            textTransform: "none",
            fontSize: 16,
            fontWeight: 600,
            color: "#160041",
            borderColor: "#D0D5DD",

            "&:hover": {
              borderColor: "#160041",
              backgroundColor: "#F9FAFB",
            },
          }}
        >
          Close
        </Button>

        <Button
          variant="outlined"
          onClick={handleReset}
          sx={{
            flex: 1,
            height: 48,
            borderRadius: "8px",
            textTransform: "none",
            fontSize: 16,
            fontWeight: 600,
            color: "#160041",
            borderColor: "#D0D5DD",

            "&:hover": {
              borderColor: "#160041",
              backgroundColor: "#F9FAFB",
            },
          }}
        >
          Reset Options
        </Button>

        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSaveDisabled}
          sx={{
            flex: 1,
            height: 48,
            borderRadius: "8px",
            textTransform: "none",
            fontSize: 16,
            fontWeight: 600,
            backgroundColor: "#160041",
            boxShadow: "none",

            "&:hover": {
              backgroundColor: "#24115E",
              boxShadow: "none",
            },
          }}
        >
          {isLoading ? "Saving..." : "Save Settings"}
        </Button>
      </DialogActions>

    </Dialog>
  );
}